import { useFormat } from 'helpers/hooks/useFormat';
import { useParams } from 'next/navigation';
import React from 'react';
import { Category } from 'shared/types/product/Category';
import { ProjectConfig } from 'types/project-config';
import SidebarFooter from '../sidebar-footer';

interface CertNavProps {
  categories: Category[];
  activeCategory: Category;
  categoryId: string;
  isActiveECard: boolean;
  allProductText?: string;
  categoryOrder: string[];
  subCategoryOrders?: Record<string, string[]>;
  projectConfig?: ProjectConfig;
}

const CertNav: React.FC<CertNavProps> = ({
  categories,
  activeCategory,
  allProductText,
  categoryId,
  isActiveECard,
  categoryOrder,
  subCategoryOrders = {},
  projectConfig,
}) => {
  const { locale } = useParams();
  const { formatMessage: formatMessageProduct } = useFormat({ name: 'product' });

  const mainCategory = categories.find((category) => category.slug === categoryId);
  const activeClass = 'font-bold text-padi-gray-darker';
  const nonActiveClass = 'hover:text-padi-gray-darker';

  const sortedMainCategory = mainCategory
    ? {
        ...mainCategory,
        subCategories: mainCategory.subCategories
          ?.sort((a, b) => {
            const indexA = categoryOrder.indexOf(a.key ?? '');
            const indexB = categoryOrder.indexOf(b.key ?? '');
            return indexA - indexB;
          })
          .map((category) => ({
            ...category,
            subCategories: category.subCategories
              ?.filter(
                (subCategory) =>
                  subCategory.slug &&
                  subCategoryOrders[category.slug as keyof typeof subCategoryOrders]?.includes(subCategory.slug),
              )
              ?.sort((a, b) => {
                const order = subCategoryOrders[category.slug as keyof typeof subCategoryOrders];
                if (!order) return 0;
                const indexA = order.indexOf(a.slug ?? '');
                const indexB = order.indexOf(b.slug ?? '');
                return indexA - indexB;
              }),
          })),
      }
    : undefined;

  return (
    <div data-id="cert-nav-container">
      <div data-id="card-type-section" className="flex flex-col gap-16 pb-30">
        <label data-id="card-type-label" className="text-lg font-semibold text-padi-gray-darkest">
          {formatMessageProduct({
            id: 'card.type',
            defaultMessage: 'Card Type',
          })}
        </label>
        <div data-id="card-type-buttons" className="flex gap-14 space-x-2">
          <a data-id="ecard-link" href={isActiveECard ? '' : `/${locale}/certification-card-ecard/`}>
            <button
              data-id="ecard-button"
              className={
                isActiveECard
                  ? 'cursor-default rounded-lg bg-padi-gray-darkest px-31 py-14 text-xs font-medium text-white focus:outline-none'
                  : 'rounded-lg border-2 border-padi-gray bg-white px-31 py-14 text-xs font-medium text-padi-gray-darkest focus:outline-none'
              }
            >
              {formatMessageProduct({
                id: 'eCard',
                defaultMessage: 'eCard',
              })}
            </button>
          </a>
          <a data-id="physical-card-link" href={isActiveECard ? `/${locale}/certification-card-physical` : ''}>
            <button
              data-id="physical-card-button"
              className={
                isActiveECard
                  ? 'rounded-lg border-2 border-padi-gray bg-white px-31 py-14 text-xs font-medium text-padi-gray-darkest focus:outline-none'
                  : 'cursor-default rounded-lg bg-padi-gray-darkest px-31 py-14 text-xs font-medium text-white focus:outline-none'
              }
            >
              {formatMessageProduct({
                id: 'physical',
                defaultMessage: 'Physical',
              })}
            </button>
          </a>
        </div>
      </div>
      <h3 data-id="all-products-heading" className="mb-10 text-base font-bold">
        <a
          data-id="all-products-link"
          href={`/${locale}${mainCategory?._url}`}
          className={activeCategory?.slug === mainCategory?.slug ? activeClass : nonActiveClass}
        >
          {allProductText}
        </a>
      </h3>
      <h3 data-id="categories-heading" className="sr-only">
        {formatMessageProduct({
          id: 'categories',
          defaultMessage: 'Categories',
        })}
      </h3>
      {sortedMainCategory?.subCategories?.map(
        (category, index) =>
          category?.slug &&
          categoryOrder.includes(category?.slug) && (
            <div key={`${category.name}-${index}`} data-id={`category-section-${category.slug}`} className="pb-20">
              <h3 data-id={`category-heading-${category.slug}`} className="mb-10 text-base font-bold">
                <a
                  data-id={`category-link-${category.slug}`}
                  href={`/${locale}${category._url}`}
                  className={activeCategory?.slug === category.slug ? activeClass : nonActiveClass}
                >
                  {category.name}
                </a>
              </h3>
              <ul
                data-id={`subcategories-list-${category.slug}`}
                role="list"
                className="space-y-8 text-sm text-gray-900"
              >
                {category?.subCategories &&
                  category?.subCategories.map((subCategory, index) => (
                    <li
                      key={`${subCategory.name}-${index}`}
                      data-id={`subcategory-item-${subCategory.slug}`}
                      className="pb-5"
                    >
                      <a
                        data-id={`subcategory-link-${subCategory.slug}`}
                        href={`/${locale}${subCategory._url}`}
                        className={activeCategory?.slug === subCategory.slug ? activeClass : nonActiveClass}
                      >
                        {subCategory.name}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          ),
      )}
      <div data-id="sidebar-footer-wrapper">
        <SidebarFooter projectConfig={projectConfig} />
      </div>
    </div>
  );
};

export default CertNav;
